/**
 * A minimal ZIP writer, store-only (no compression): the files that go in are WebP and JSON, already
 * compressed, so deflating them again would cost CPU for nothing. It exists because a track's tile
 * export is hundreds of files and the browser cannot download a folder: one `.zip` is the only
 * package a person can unzip into the game's `assets/` folder.
 *
 * The format is the baseline one (PKZIP 2.0, method 0): a local header + data per file, then the
 * central directory and the end-of-central-directory record. The timestamp is fixed (1980-01-01) so
 * exporting the same track twice gives the same bytes.
 */

const CRC_TABLE = (() => {
  const table = new Uint32Array(256);
  for (let n = 0; n < 256; n++) {
    let c = n;
    for (let k = 0; k < 8; k++) c = c & 1 ? 0xedb88320 ^ (c >>> 1) : c >>> 1;
    table[n] = c >>> 0;
  }
  return table;
})();

export function crc32(bytes) {
  let crc = 0xffffffff;
  for (let i = 0; i < bytes.length; i++) crc = CRC_TABLE[(crc ^ bytes[i]) & 0xff] ^ (crc >>> 8);
  return (crc ^ 0xffffffff) >>> 0;
}

const DOS_TIME = 0;
const DOS_DATE = 0x0021; // 1980-01-01

/**
 * Packs `files` (`{ path, data: Uint8Array }`, paths with forward slashes) into a ZIP Blob.
 * Sizes are 32-bit: an archive over 4 GB would need ZIP64, and a track is nowhere near it.
 */
export function makeZip(files) {
  const encoder = new TextEncoder();
  const entries = files.map((file) => ({
    name: encoder.encode(file.path),
    data: file.data,
    crc: crc32(file.data),
  }));

  const localParts = [];
  let offset = 0;
  for (const entry of entries) {
    const header = new Uint8Array(30 + entry.name.length);
    const view = new DataView(header.buffer);
    view.setUint32(0, 0x04034b50, true);       // local file header
    view.setUint16(4, 20, true);                // version needed
    view.setUint16(6, 0x0800, true);            // UTF-8 names
    view.setUint16(8, 0, true);                 // method: stored
    view.setUint16(10, DOS_TIME, true);
    view.setUint16(12, DOS_DATE, true);
    view.setUint32(14, entry.crc, true);
    view.setUint32(18, entry.data.length, true); // compressed
    view.setUint32(22, entry.data.length, true); // uncompressed
    view.setUint16(26, entry.name.length, true);
    view.setUint16(28, 0, true);                 // extra field
    header.set(entry.name, 30);
    localParts.push(header, entry.data);
    entry.offset = offset;
    offset += header.length + entry.data.length;
  }

  const centralParts = [];
  let centralSize = 0;
  for (const entry of entries) {
    const header = new Uint8Array(46 + entry.name.length);
    const view = new DataView(header.buffer);
    view.setUint32(0, 0x02014b50, true);       // central directory header
    view.setUint16(4, 20, true);                // version made by
    view.setUint16(6, 20, true);                // version needed
    view.setUint16(8, 0x0800, true);            // UTF-8 names
    view.setUint16(10, 0, true);                // method: stored
    view.setUint16(12, DOS_TIME, true);
    view.setUint16(14, DOS_DATE, true);
    view.setUint32(16, entry.crc, true);
    view.setUint32(20, entry.data.length, true);
    view.setUint32(24, entry.data.length, true);
    view.setUint16(28, entry.name.length, true);
    view.setUint16(30, 0, true);                 // extra
    view.setUint16(32, 0, true);                 // comment
    view.setUint16(34, 0, true);                 // disk number
    view.setUint16(36, 0, true);                 // internal attributes
    view.setUint32(38, 0, true);                 // external attributes
    view.setUint32(42, entry.offset, true);
    header.set(entry.name, 46);
    centralParts.push(header);
    centralSize += header.length;
  }

  const end = new Uint8Array(22);
  const view = new DataView(end.buffer);
  view.setUint32(0, 0x06054b50, true);           // end of central directory
  view.setUint16(4, 0, true);
  view.setUint16(6, 0, true);
  view.setUint16(8, entries.length, true);
  view.setUint16(10, entries.length, true);
  view.setUint32(12, centralSize, true);
  view.setUint32(16, offset, true);
  view.setUint16(20, 0, true);

  return new Blob([...localParts, ...centralParts, end], { type: 'application/zip' });
}
